import { useCallback, useMemo } from 'react'
import { AddressFragment, CustomerType, GuestFragment, GuestType, IdentificationDocumentFragment, PersonIdentificationDocumentFragment, ReservedResourcePhase } from 'src/api/api.generated'
import GuestLookup from 'src/app/components/customer-lookup/guest-lookup'
import { compareDates, isValidDate } from 'src/utils/date'
import { Context } from '../../../../../context/reservation-context.types'
import { GuestAddress, GuestIdentificationDocument, GuestOccupancy } from '../../../../../types/guests.types'
import { fragmentToGuestAddress } from '../../../../../utils/guests.utils'

export type OccupancyGuestLookupProps = {
  context: Context
  value: GuestOccupancy
  onChange: (value: GuestOccupancy) => void
  error?: boolean
  disabled?: boolean
  size?: 'small' | 'medium'
}

export default function OccupancyGuestLookup({ context, value, onChange, error, disabled, size }: OccupancyGuestLookupProps) {
  const lookupValue: GuestFragment | null = useMemo(() => {
    return guestOccupancyToGuestFragment(value)
  }, [value])

  const onLookupChange = useCallback(
    (customer: GuestFragment | null) => {
      onChange({
        ...value, //
        type: getTypeOrDefault(value, context),
        guestId: customer?.id || null,
        language: customer?.language || null,
        lastName: customer?.lastName || null,
        firstName: customer?.firstName || null,
        gender: customer?.person?.gender ?? null,
        birthDate: customer?.person?.birthDate ?? null,
        birthAddress: fragmentToGuestAddress(customer?.person?.birthAddress),
        address: fragmentToGuestAddress(customer?.address),
        nationalityId: customer?.person?.nationalityId ?? null,
        cityTaxExoneration: customer?.cityTax?.exoneration ?? null,
        identificationDocument: customer?.person?.identificationDocument
          ? {
              documentId: customer.person.identificationDocument.documentId,
              number: customer.person.identificationDocument.number,
              expirationDate: customer.person.identificationDocument.expirationDate,
              countryId: customer.person.identificationDocument.countryId,
              city: customer.person.identificationDocument.city ?? null,
            }
          : null,
      })
    },
    [context, value, onChange, context],
  )

  const onGuestCreate = useCallback(
    (name: { lastName: string; firstName: string }) => {
      onChange({
        ...value, //
        type: getTypeOrDefault(value, context),
        guestId: '',
        language: 'it',
        lastName: name.lastName,
        firstName: name.firstName,
        gender: null,
        birthDate: null,
        birthAddress: null,
        address: null,
        nationalityId: null,
        identificationDocument: null,
      })
    },
    [onChange, value],
  )

  return (
    <GuestLookup //
      value={lookupValue}
      onChange={onLookupChange}
      onGuestCreateClick={onGuestCreate}
      error={error}
      disabled={disabled}
      size={size}
    />
  )
}

function guestOccupancyToGuestFragment(value: GuestOccupancy): GuestFragment | null {
  if (value.guestId === null) return null
  const f: GuestFragment = {
    id: value.guestId,
    language: value.language ?? 'it',
    auditable: { state: 'ACTIVE' },
    type: CustomerType.PERSON,
    name: `${value.lastName ?? ''} ${value.firstName ?? ''}`,
    address: guestAddressToFragment(value.address),
    firstName: value.firstName,
    lastName: value.lastName,
    person: {
      gender: value.gender,
      birthAddress: guestAddressToFragment(value.birthAddress),
      birthDate: value.birthDate,
      identificationDocument: guestIdentificationDocumentToFragment(value.identificationDocument),
      nationalityId: value.nationalityId,
    },
  }
  return f
}

function guestAddressToFragment(a: GuestAddress | null): AddressFragment | null {
  if (a === null) return null
  const f: AddressFragment = {
    countryId: a.countryId,
    municipalityId: a.municipalityId,
  }
  return f
}

function guestIdentificationDocumentToFragment(i: GuestIdentificationDocument | null): PersonIdentificationDocumentFragment | null {
  if (i === null || i.countryId === null || i.documentId === null || i.expirationDate === null || i.number === null) return null

  const f: PersonIdentificationDocumentFragment = {
    countryId: i.countryId,
    documentId: i.documentId,
    expirationDate: i.expirationDate,
    number: i.number,
    city: i.city,
  }
  return f
}

function getTypeOrDefault(value: GuestOccupancy, context: Context): GuestType {
  if (value.type) return value.type
  const start = value.start
  if (!isValidDate(start)) return GuestType.SINGLE_GUEST

  let anyFamilyHeadCheckingIn = false
  let anyGroupHeadCheckingIn = false

  Array.from(context.reservedResources.keys()).forEach((key) => {
    const guests = context.reservedResources.get(key)?.guests ?? []
    guests.forEach((guest) => {
      if (
        (guest.type === GuestType.HEAD_OF_FAMILY || guest.type === GuestType.GROUP_HEAD) && //
        guest.start &&
        guest.phase !== ReservedResourcePhase.CHECKOUT &&
        compareDates(guest.start, start) === 0
      ) {
        if (guest.type === GuestType.HEAD_OF_FAMILY) anyFamilyHeadCheckingIn = true
        if (guest.type === GuestType.GROUP_HEAD) anyGroupHeadCheckingIn = true
      }
    })
  })

  return anyFamilyHeadCheckingIn ? GuestType.FAMILY : anyGroupHeadCheckingIn ? GuestType.GROUP_MEMBER : GuestType.SINGLE_GUEST
}
